import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL || '';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('ariaone-token'));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('ariaone-refresh-token'));
  const [loading, setLoading] = useState(true);
  const refreshing = useRef(false);

  // Persist tokens
  useEffect(() => {
    if (token) localStorage.setItem('ariaone-token', token);
    else localStorage.removeItem('ariaone-token');
  }, [token]);
  useEffect(() => {
    if (refreshToken) localStorage.setItem('ariaone-refresh-token', refreshToken);
    else localStorage.removeItem('ariaone-refresh-token');
  }, [refreshToken]);

  // Normalize user object from any vendor response shape
  const normalizeUser = useCallback((raw) => {
    if (!raw || typeof raw !== 'object') return null;
    return {
      id: raw.id || raw.user_id || raw._id || '',
      email: raw.email || '',
      first_name: raw.first_name || raw.firstName || raw.given_name || '',
      last_name: raw.last_name || raw.lastName || raw.family_name || '',
      name: raw.name || raw.full_name || `${raw.first_name || raw.firstName || ''} ${raw.last_name || raw.lastName || ''}`.trim() || '',
      phone: raw.phone || raw.phone_number || '',
      role: raw.role || raw.user_role || '',
      app_role: raw.app_role || '',
      access_mapping_id: raw.access_mapping_id || raw.mapping_id || null,
      organization: raw.organization || raw.org || raw.practice || '',
      avatar: raw.avatar || raw.avatar_url || raw.picture || '',
      // Authoritative capability map (server-computed). See permissions.js.
      permissions: raw.permissions && typeof raw.permissions === 'object' ? raw.permissions : null,
    };
  }, []);

  // Fetch user profile from /api/auth/me, then enrich with per-org app_role
  // resolved via /api/user-access/me (this triggers first-login bootstrap).
  const fetchProfile = useCallback(async (accessToken) => {
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const text = await res.text();
        let data; try { data = JSON.parse(text); } catch { data = {}; }
        const raw = data.user || data;
        const normalized = normalizeUser(raw);
        // Resolve app_role (never throws — missing mapping just yields read_only)
        try {
          const rr = await fetch(`${API}/api/user-access/me`, {
            headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          });
          if (rr.ok) {
            const rd = await rr.json().catch(() => ({}));
            if (rd?.app_role) {
              normalized.app_role = rd.app_role;
              normalized.access_mapping_id = rd.mapping_id || null;
            }
            // Authoritative capability map (server-computed from POLICY).
            // UI prefers this over local role-map so vendor-authorized
            // roles aren't blocked by out-of-sync client aliases.
            if (rd?.permissions && typeof rd.permissions === 'object') {
              normalized.permissions = rd.permissions;
            }
            // Persist effective org id so httpClient can attach X-Organization-Id
            // on every downstream API call (prevents scheduler 403 parity drift).
            if (rd?.org_id) {
              try { localStorage.setItem('ariaone-org-id', rd.org_id); } catch { /* noop */ }
              normalized.org_id = rd.org_id;
            }
          }
        } catch { /* network — leave app_role undefined, permissions fall back to role */ }
        setUser(normalized);
      }
    } catch { /* network error, keep existing user state */ }
  }, [normalizeUser]);

  const signOut = useCallback(() => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem('ariaone-token');
    localStorage.removeItem('ariaone-refresh-token');
    localStorage.removeItem('ariaone-org-id');
    try { localStorage.removeItem('ariaone-demo-mode'); } catch {}
  }, []);

  // Try to refresh the access token
  const tryRefresh = useCallback(async () => {
    const rt = refreshToken || localStorage.getItem('ariaone-refresh-token');
    if (!rt || refreshing.current) return false;
    refreshing.current = true;
    try {
      const res = await fetch(`${API}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) { signOut(); return false; }
      let data = {};
      try { data = await res.json(); } catch { data = {}; }
      if (data.access_token) {
        setToken(data.access_token);
        if (data.refresh_token) setRefreshToken(data.refresh_token);
        return true;
      }
      signOut();
      return false;
    } catch {
      signOut();
      return false;
    } finally {
      refreshing.current = false;
    }
  }, [refreshToken, signOut]);

  // Auth-aware fetch: on 401, try refresh once then retry once
  const authFetch = useCallback(async (url, opts = {}) => {
    const currentToken = token || localStorage.getItem('ariaone-token');
    const isFormData = opts.body instanceof FormData;
    const headers = { ...(opts.headers || {}), ...(isFormData ? {} : { 'Content-Type': 'application/json' }) };
    if (currentToken) headers['Authorization'] = `Bearer ${currentToken}`;

    let res = await fetch(url, { ...opts, headers });
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        const newToken = localStorage.getItem('ariaone-token');
        headers['Authorization'] = `Bearer ${newToken}`;
        res = await fetch(url, { ...opts, headers });
      }
    }
    return res;
  }, [token, tryRefresh]);

  // Restore session on mount
  useEffect(() => {
    const restore = async () => {
      const t = localStorage.getItem('ariaone-token');
      if (!t) { setLoading(false); return; }
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const text = await res.text();
          let data; try { data = JSON.parse(text); } catch { data = {}; }
          const normalized = normalizeUser(data.user || data);
          // Enrich with per-org app_role (non-fatal on failure)
          try {
            const rr = await fetch(`${API}/api/user-access/me`, {
              headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
            });
            if (rr.ok) {
              const rd = await rr.json().catch(() => ({}));
              if (rd?.app_role) {
                normalized.app_role = rd.app_role;
                normalized.access_mapping_id = rd.mapping_id || null;
              }
              if (rd?.permissions && typeof rd.permissions === 'object') {
                normalized.permissions = rd.permissions;
              }
              if (rd?.org_id) {
                try { localStorage.setItem('ariaone-org-id', rd.org_id); } catch { /* noop */ }
                normalized.org_id = rd.org_id;
              }
            }
          } catch { /* noop */ }
          setUser(normalized);
          setToken(t);
        } else if (res.status === 401) {
          const refreshed = await tryRefresh();
          if (refreshed) {
            const t2 = localStorage.getItem('ariaone-token');
            const res2 = await fetch(`${API}/api/auth/me`, {
              headers: { Authorization: `Bearer ${t2}`, 'Content-Type': 'application/json' },
            });
            if (res2.ok) {
              const text2 = await res2.text();
              let data2; try { data2 = JSON.parse(text2); } catch { data2 = {}; }
              setUser(normalizeUser(data2.user || data2));
            } else { signOut(); }
          } else { signOut(); }
        } else { signOut(); }
      } catch {
        // Network error — keep token in case offline, clear user
        setUser(null);
      }
      setLoading(false);
    };
    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (res.status === 429) throw new Error('Too many requests, please wait and try again.');
    if (!res.ok) {
      let msg = 'Invalid email or password';
      try {
        const errData = await res.json();
        const d = errData.detail;
        if (typeof d === 'string') msg = d;
        else if (d?.message) msg = d.message;
        else if (errData.message) msg = errData.message;
      } catch { /* body unreadable */ }
      throw new Error(msg);
    }
    const data = await res.json();
    const accessToken = data.access_token || data.token;
    setToken(accessToken);
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    // Set user from login response if available, then enrich from /me
    if (data.user) setUser(normalizeUser(data.user));
    if (accessToken) await fetchProfile(accessToken);
    return data;
  }, [normalizeUser, fetchProfile]);

  const register = useCallback(async (payload) => {
    const res = await fetch(`${API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.status === 429) throw new Error('Too many requests, please wait and try again.');
    if (!res.ok) {
      let msg = 'Registration failed';
      try {
        const errData = await res.json();
        const d = errData.detail;
        if (typeof d === 'string') msg = d;
        else if (d?.message) msg = d.message;
        else if (errData.message) msg = errData.message;
      } catch { /* body unreadable */ }
      throw new Error(msg);
    }
    const data = await res.json();
    if (data.access_token || data.token) {
      const accessToken = data.access_token || data.token;
      setToken(accessToken);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      if (data.user) setUser(normalizeUser(data.user));
      if (accessToken) await fetchProfile(accessToken);
    }
    return data;
  }, [normalizeUser, fetchProfile]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    register,
    signOut,
    authFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
